import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, RefreshCw, Trash2, CheckCircle2, AlertCircle, Clock } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import PaymentForm from '../components/PaymentForm';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { formatDateForCountry } from '../utils/countries';
import './Dashboard.css';

function MyDomains({ user, onLogout }) {
  const navigate = useNavigate();
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDomainForRenew, setSelectedDomainForRenew] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filter, setFilter] = useState('all');
  const { t } = useTranslation();

  useEffect(() => {
    fetchDomains();
  }, []);

  const fetchDomains = async () => {
    try {
      const response = await axios.get('/user/domains');
      setDomains(response.data.domains);
    } catch (err) {
      setError(t('error.loadingDomains'));
    } finally {
      setLoading(false);
    }
  };

  const handleRenewSubscription = (domain) => {
    setSelectedDomainForRenew(domain);
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = () => {
    setShowPaymentModal(false);
    setSelectedDomainForRenew(null);
    setSuccess(t('success.renewSuccess'));
    fetchDomains();
  };

  const handlePaymentCancel = () => {
    setShowPaymentModal(false);
    setSelectedDomainForRenew(null);
  };

  const handleDeleteDomain = async (domainId) => {
    if (!window.confirm(t('confirm.deleteDomain'))) return;
    try {
      await axios.delete(`/user/domains/${domainId}`);
      setSuccess(t('success.domainDeleted'));
      fetchDomains();
    } catch (err) {
      setError(err.response?.data?.error || t('error.deleteDomain'));
    }
  };

  const getSubscriptionStatus = (domain) => {
    if (!domain.subscription_id) {
      return { status: 'pending', label: t('subscription.pending'), className: 'danger', icon: AlertCircle };
    }
    const daysRemaining = parseInt(domain.days_remaining);
    if (domain.subscription_status === 'expired' || daysRemaining < 0) {
      return { status: 'expired', label: t('subscription.expired'), className: 'danger', icon: AlertCircle };
    }
    if (daysRemaining <= 7) {
      return { status: 'expiring', label: t('subscription.daysRemaining', { count: daysRemaining }), className: 'warning', icon: Clock };
    }
    return { status: 'active', label: t('subscription.daysRemaining', { count: daysRemaining }), className: 'success', icon: CheckCircle2 };
  };

  const getFilteredDomains = () => {
    if (filter === 'all') return domains;
    return domains.filter(domain => {
      const status = getSubscriptionStatus(domain);
      if (filter === 'active') return status.status === 'active' || status.status === 'expiring';
      if (filter === 'expired') return status.status === 'expired' || status.status === 'pending';
      return true;
    });
  };

  const filteredDomains = getFilteredDomains();

  if (loading) {
    return (
      <div className="dashboard-layout">
        <Sidebar user={user} onLogout={onLogout} />
        <div className="dashboard-main">
          <div className="loading">{t('loading.text')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      <Sidebar user={user} onLogout={onLogout} />
      <main className="dashboard-main">
        <header className="dashboard-header">
          <div>
            <h1>{t('domains.title')}</h1>
            <p>{t('domains.description')}</p>
          </div>
          <button className="btn-primary" onClick={() => navigate('/dashboard')}>
            {t('action.backToDashboard')}
          </button>
        </header>
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}
        <div className="tabs">
          <button className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>
            {t('filter.all')} ({domains.length})
          </button>
          <button className={filter === 'active' ? 'active' : ''} onClick={() => setFilter('active')}>
            {t('filter.active')} ({domains.filter(d => ['active','expiring'].includes(getSubscriptionStatus(d).status)).length})
          </button>
          <button className={filter === 'expired' ? 'active' : ''} onClick={() => setFilter('expired')}>
            {t('filter.expired')} ({domains.filter(d => ['expired','pending'].includes(getSubscriptionStatus(d).status)).length})
          </button>
        </div>
        <section className="section domains_section">
          {filteredDomains.length === 0 ? (
            <div className="empty-state">
              <p>{t('domains.noResults')}</p>
            </div>
          ) : (
            <div className="domains-grid">
              {filteredDomains.map(domain => {
                const subscriptionStatus = getSubscriptionStatus(domain);
                const StatusIcon = subscriptionStatus.icon;
                return (
                  <div key={domain.id} className="domain-card">
                    <div className="domain-card-header">
                      <h3 className="domain-url">{domain.domain_url}</h3>
                      <span className={`badge ${subscriptionStatus.className}`}>
                        <StatusIcon size={14} strokeWidth={2} style={{marginRight: '4px', verticalAlign: 'middle'}} />
                        {subscriptionStatus.label}
                      </span>
                    </div>
                    <div className="domain-card-body">
                      <div className="domain-info-row">
                        <span className="info-label">{t('domain.monitoring')}:</span>
                        <span className={`badge ${domain.is_monitoring ? 'success' : 'secondary'}`}>
                          {domain.is_monitoring ? t('filter.active') : t('filter.passive')}
                        </span>
                      </div>
                      <div className="domain-info-row">
                        <span className="info-label">{t('domain.checks24h')}:</span>
                        <span className="info-value">{domain.checks_24h || 0}</span>
                      </div>
                      {domain.errors_24h >= 0 && (
                        <div className="domain-info-row">
                          <span className="info-label">{t('domain.errors24h')}:</span>
                          <span className="error-text">{domain.errors_24h}</span>
                        </div>
                      )}
                      {domain.subscription_end_date && (
                        <div className="domain-info-row">
                          <span className="info-label">{t('domain.expiryDate')}:</span>
                          <span className="info-value">
                              {formatDateForCountry(domain.subscription_end_date, user?.country)}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="domain-card-actions">
                      <button className="btn-small btn-primary" onClick={() => navigate(`/domains/${domain.id}`)}>
                        <Eye size={14} strokeWidth={2} style={{marginRight: '4px', verticalAlign: 'middle'}} />
                        {t('action.details')}
                      </button>
                      {(!domain.subscription_id || domain.subscription_status === 'expired') ? (
                        <button className="btn-small btn-success" onClick={() => handleRenewSubscription(domain)} style={{padding: '0.5rem'}}>
                          <RefreshCw size={14} strokeWidth={2} style={{marginRight: '4px', verticalAlign: 'middle'}} />
                          {t('action.payNow')}
                        </button>
                      ) : (
                        <button className="btn-small btn-secondary" onClick={() => handleRenewSubscription(domain)}>
                          <RefreshCw size={14} strokeWidth={2} style={{marginRight: '4px', verticalAlign: 'middle'}} />
                          {t('action.renew')}
                        </button>
                      )}
                      <button className="btn-small btn-danger" onClick={() => handleDeleteDomain(domain.id)}>
                        <Trash2 size={14} strokeWidth={2} style={{marginRight: '4px', verticalAlign: 'middle'}} />
                        {t('action.delete')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {showPaymentModal && selectedDomainForRenew && (
          <div className="modal-overlay">
            <div className="modal-content modal-largex" onClick={(e) => e.stopPropagation()}>
              <PaymentForm
                domain={selectedDomainForRenew}
                onSuccess={handlePaymentSuccess}
                onCancel={handlePaymentCancel}
              />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default MyDomains;
